import { useEffect, useState } from "react";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  VehicleClaimProcessStage,
  type EventClaim,
} from "../../../types/eventClaim.types";
import { REPOSSESSION_STATUS } from "../../../types/repossession.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { ModalEventClaimEdit } from "./ModalEventClaimEdit";
import { ChangeClaimStatus } from "./ChangeClaimStatus";
import { DeleteEventClaim } from "./DeleteEventClaim";
import {
  deleteEventClaim,
  getPagedEventClaim,
} from "../../../common/services/eventCliam.api";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { Modal } from "flowbite-react";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import Loading from "@/common/components/ui/loading/Loading";

const STATUS_LABELS: Record<VehicleClaimProcessStage, string> = {
  [VehicleClaimProcessStage.NOTICE]: "Comunicado",
  [VehicleClaimProcessStage.NOTICE_VALIDATION]: "Validação Comunicado",
  [VehicleClaimProcessStage.ADJUSTMENT]: "Regulagem",
  [VehicleClaimProcessStage.ADJUSTMENT_SUPERVISION]: "Regulagem Supervisão",
  [VehicleClaimProcessStage.INVESTIGATION]: "Sindicância",
  [VehicleClaimProcessStage.INVESTIGATION_VALIDATION]:
    "Validação da Sindicância",
  [VehicleClaimProcessStage.PURCHASE]: "Compras",
  [VehicleClaimProcessStage.IN_WORKSHOP]: "Em Oficina",
  [VehicleClaimProcessStage.QUALITY_INSPECTION]: "Inspeção de Qualidade",
  [VehicleClaimProcessStage.SOLIDY_EXPERIENCE_SURVEY]:
    "Experiência Solidy - Pesquisa de Satisfação",
};

const formatPhoneNumber = (phone: string): string => {
  // Remove non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Check if it's a valid length
  if (cleaned.length === 11) {
    // Mobile phone format: (XX) XXXXX-XXXX
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
      2,
      7
    )}-${cleaned.substring(7)}`;
  } else if (cleaned.length === 10) {
    // Landline format: (XX) XXXX-XXXX
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
      2,
      6
    )}-${cleaned.substring(6)}`;
  }

  // Return original if format can't be determined
  return phone;
};

const HEADERS: Record<string, string> = {
  companyId: "Cidade",
  associateName: "Nome",
  whatsapp: "WhatsApp",
  licensePlate: "Placa do Veículo",
  status: "Status",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapData = (data: EventClaim[]): any[] => {
  const VEHICLE_CLAIM_PROCESS_STAGE_LABELS: Record<
    VehicleClaimProcessStage,
    string
  > = {
    [VehicleClaimProcessStage.NOTICE]: "Comunicado",
    [VehicleClaimProcessStage.NOTICE_VALIDATION]: "Validação Comunicado",
    [VehicleClaimProcessStage.ADJUSTMENT]: "Regulagem",
    [VehicleClaimProcessStage.ADJUSTMENT_SUPERVISION]: "Regulagem Supervisão",
    [VehicleClaimProcessStage.INVESTIGATION]: "Sindicância",
    [VehicleClaimProcessStage.INVESTIGATION_VALIDATION]:
      "Validação da Sindicância",
    [VehicleClaimProcessStage.PURCHASE]: "Compras",
    [VehicleClaimProcessStage.IN_WORKSHOP]: "Em Oficina",
    [VehicleClaimProcessStage.QUALITY_INSPECTION]: "Inspeção de Qualidade",
    [VehicleClaimProcessStage.SOLIDY_EXPERIENCE_SURVEY]:
      "Experiência Solidy - Pesquisa de Satisfação",
  };

  return data.map((item) => ({
    companyId: getCompanySigla(item.companyId as string) || "",
    licensePlate: item.licensePlate || "",
    whatsapp: item.whatsapp ? formatPhoneNumber(item.whatsapp) : "",
    associateName: item.associateName ? (
      <a href={`mailto:${item.associateName}`}>{item.associateName}</a>
    ) : (
      ""
    ),
    status: item.status
      ? VEHICLE_CLAIM_PROCESS_STAGE_LABELS[
          item.status as VehicleClaimProcessStage
        ] ||
        STATUS_LABELS[item.status as REPOSSESSION_STATUS] ||
        item.status
      : "",
    _original: item,
  }));
};

function EventClaim() {
  const [data, setData] = useState<EventClaim[]>([]);
  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState({ itemsPerPage: 10, totalItems: 0 });
  const { notifyError, notifySuccess } = useToastContext();
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const isAction = (eventName: string) => action.eventName === eventName;
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
    itemsPerPage: "10",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [eventClaimToDelete, setEventClaimToDelete] =
    useState<EventClaim | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleDeleteEventClaim = async () => {
    if (!eventClaimToDelete || !eventClaimToDelete._id) {
      notifyError("Evento não encontrado.");
      setShowConfirmModal(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await deleteEventClaim(eventClaimToDelete._id);
      if (!response.ok) {
        notifyError(
          response.message ||
            "Erro ao excluir evento. Por favor, tente novamente."
        );
      } else {
        setData((prevData) =>
          prevData.filter((item) => item._id !== eventClaimToDelete._id)
        );
        notifySuccess("Evento excluído com sucesso!");
      }
    } catch (error) {
      notifyError("Erro ao excluir evento. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
      setEventClaimToDelete(null);
    }
  };

  const handleSaveInsuranceClaim = (insuranceClaim: EventClaim) => {
    if (
      insuranceClaim._id &&
      data.some((item) => item._id === insuranceClaim._id)
    ) {
      setData((prevData) =>
        prevData.map((item) =>
          item._id === insuranceClaim._id ? insuranceClaim : item
        )
      );
      notifySuccess("Sinistro atualizado com sucesso!");
    } else {
      setData((prevData) => [...prevData, insuranceClaim]);
      notifySuccess("Novo sinistro adicionado com sucesso!");
    }
  };

  const company = useCompanyStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getPagedEventClaim({
          status: filters.status,
          search: filters.search,
          orderBy: filters.orderBy,
          page: page,
          itemsPerPage: Number(filters.itemsPerPage),
        });
        if (!response.ok)
          return notifyError(response.message || "Erro ao carregar os dados");
        setData(response.data);
        setRows({
          itemsPerPage: Number(filters.itemsPerPage) || 10,
          totalItems: response.totalItems,
        });
      } catch (error) {
        console.error("Erro ao buscar os dados:", error);
        notifyError("Erro ao carregar os dados");
      }
    };
    fetchData();
  }, [filters, page, company, notifyError]);

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({
          eventName: "editInsuranceClaim",
          data: data._original,
        }),
      permission: "edit:leads",
    },
    {
      label: "Trocar Status",
      action: (data) =>
        setAction({
          eventName: "changeClaimStatus",
          data: data._original,
        }),
      permission: "edit:leads",
    },
    {
      label: "Excluir",
      action: (data) => {
        setEventClaimToDelete(data._original);
        setShowConfirmModal(true);
      },
      permission: "edit:leads",
    },
  ];

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Evento"
          placeholder="Digite o telefone, e-mail, WhatsApp ou placa do veículo"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          tableData={data}
          newItemLabel="Novo Evento"
          onNewItemClick={() =>
            setAction({ eventName: "editInsuranceClaim", data: undefined })
          }
          permission="createRenterMotoclub:motoClub"
          entity="EventClaim"
        />
        <GenericTable
          entity="leads"
          headers={HEADERS}
          data={mapData(data)}
          actions={actions}
          setFilters={({ orderBy }) => setFilters((f) => ({ ...f, orderBy }))}
        />
        {isLoading && <Loading />}
        <TableFooter
          page={page || 1}
          totalItems={rows.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("editInsuranceClaim") && (
          <ModalEventClaimEdit
            eventClaim={action.data as EventClaim | undefined}
            onClose={cleanActionData}
            onSave={handleSaveInsuranceClaim}
          />
        )}
        {isAction("changeClaimStatus") && (
          <ChangeClaimStatus
            eventClaim={action.data as EventClaim | undefined}
            onClose={cleanActionData}
            onSave={handleSaveInsuranceClaim}
          />
        )}
        {isAction("deleteEventClaim") && (
          <DeleteEventClaim
            eventClaim={action.data as EventClaim | undefined}
            onClose={cleanActionData}
            onDelete={handleDeleteEventClaim}
          />
        )}

        <Modal
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          size="md"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold text-[#e44131] mb-2">
              Confirmar Exclusão
            </h2>
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir este evento?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-200 px-4 py-2 rounded"
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded mr-2"
                onClick={handleDeleteEventClaim}
                disabled={isLoading}
              >
                Sim, excluir
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default EventClaim;
